import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Gift } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { trackConversion, trackEvent } from "@/components/AnalyticsTracker";

export default function Register() {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [restaurant, setRestaurant] = useState("");
  const [phone, setPhone] = useState("");
  const [promoCode, setPromoCode] = useState(searchParams.get("promo") || "");
  const [promoState, setPromoState] = useState(null); // null | 'checking' | {valid, remaining, months_free, reason}
  const [loading, setLoading] = useState(false);

  // Validate promo on change (debounced)
  useEffect(() => {
    if (!promoCode.trim()) { setPromoState(null); return; }
    const code = promoCode.trim().toUpperCase();
    setPromoState({ status: "checking" });
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/promo/validate?code=${encodeURIComponent(code)}`);
        setPromoState({ status: "done", ...r.data });
      } catch {
        setPromoState({ status: "done", valid: false });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [promoCode]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name, promoCode.trim() || undefined, { restaurant_name: restaurant.trim() || undefined, phone: phone.trim() || undefined, lang: (i18n.language || "fr").slice(0, 2) });
      trackEvent("sign_up_success", { method: "email", promo: promoCode.trim() ? "yes" : "no" });
      trackConversion("register", 0);
      trackConversion("start_trial", 0);
      toast.success(t("auth.register_success"));
      navigate("/onboarding");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("auth.register_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-6">
      <div className="w-full max-w-md page-enter">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-c flex items-center justify-center text-white font-display text-lg">O</div>
            <span className="font-display text-2xl">Oresto</span>
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="card-c">
          <h1 className="font-display text-3xl">
            {promoState?.status === "done" && promoState.valid ? (
              <>{t("auth.register_title_promo_1")} <span className="font-serif-italic text-primary-c">{t("auth.register_title_promo_accent", { months: promoState.months_free })}</span></>
            ) : (
              <>{t("auth.register_title_1")} <span className="font-serif-italic text-primary-c">{t("auth.register_title_accent")}</span></>
            )}
          </h1>
          <p className="text-text-secondary text-sm mt-1">{t("auth.register_subtitle")}</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-text-secondary block mb-2">{t("auth.name")}</label>
              <input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} className="input-c" placeholder={t("auth.name_placeholder")} />
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary block mb-2">{t("auth.email")}</label>
              <input data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-c" placeholder={t("auth.email_placeholder")} />
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary block mb-2">{t("auth.password")}</label>
              <input data-testid="register-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="input-c" placeholder={t("auth.password_placeholder_8")} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-text-secondary block mb-2">{t("auth.restaurant_label")}</label>
                <input data-testid="register-restaurant-input" maxLength={120} value={restaurant} onChange={(e) => setRestaurant(e.target.value)} className="input-c" placeholder={t("auth.restaurant_placeholder")} />
              </div>
              <div>
                <label className="text-sm font-medium text-text-secondary block mb-2">{t("auth.phone_label")}</label>
                <input data-testid="register-phone-input" type="tel" maxLength={30} value={phone} onChange={(e) => setPhone(e.target.value)} className="input-c" placeholder={t("auth.phone_placeholder")} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary block mb-2 flex items-center gap-1.5">
                <Gift className="w-4 h-4 text-secondary-c" /> {t("auth.promo_label")}
              </label>
              <input
                data-testid="register-promo-input"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="input-c uppercase"
                placeholder="AMBASSADEUR2026"
                autoComplete="off"
              />
              {promoState?.status === "done" && promoState.valid && (
                <p className="text-xs text-primary-c mt-1.5 flex items-center gap-1" data-testid="promo-valid">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {t("auth.promo_ok", { months: promoState.months_free, count: promoState.remaining })}
                </p>
              )}
              {promoState?.status === "done" && !promoState.valid && promoCode && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1" data-testid="promo-invalid">
                  <AlertCircle className="w-3.5 h-3.5" /> {promoState.reason === "exhausted" ? t("auth.promo_exhausted") : t("auth.promo_invalid")}
                </p>
              )}
            </div>
            <button data-testid="register-submit-button" disabled={loading} className="btn-primary w-full">
              {loading ? t("auth.creating") : t("auth.register_button")}
            </button>
          </form>
          <p className="text-sm text-text-secondary mt-5 text-center">
            {t("auth.have_account")} <Link to="/login" className="text-primary-c font-semibold" data-testid="register-to-login-link">{t("auth.login_link")}</Link>
          </p>
          <p className="text-[11px] text-text-secondary text-center mt-4 leading-relaxed">
            {t("auth.terms_1")} <Link to="/cgv" className="underline">{t("auth.terms_link")}</Link> {t("auth.terms_2")} <Link to="/confidentialite" className="underline">{t("auth.privacy_link")}</Link>.
          </p>
        </div>
        <nav className="legal-mini-links">
          <Link to="/cgv">{t("footer.terms")}</Link>
          <Link to="/confidentialite">{t("footer.privacy")}</Link>
          <Link to="/mentions-legales">{t("footer.notice")}</Link>
        </nav>
      </div>
    </div>
  );
}
